'use client';

import { useTranslation } from 'react-i18next';

/**
 * Delta (Nadena) bespoke Privacy Policy.
 *
 * Nadena's live site has no published privacy page; this content is therefore
 * a tailored policy grounded in their real membership terms — notably the
 * media/documentation consent (terms clause #10) — and aligned with Saudi PDPL
 * basics. The operating entity is شركة تطوير للخدمات التعليمية (Tatweer
 * Educational Services). Arabic is authoritative; an English rendering follows.
 */

export function DeltaPrivacyPolicy() {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  return isArabic ? <ArabicPrivacy /> : <EnglishPrivacy />;
}

function ArabicPrivacy() {
  return (
    <div dir="rtl" className="prose prose-lg dark:prose-invert max-w-none text-right">
      <h2 className="mt-8 mb-4 text-2xl font-bold">مقدمة</h2>
      <p>
        توضح سياسة الخصوصية هذه كيفية جمع نادينا (المُشغَّلة من قِبل شركة تطوير
        للخدمات التعليمية) لمعلوماتك الشخصية واستخدامها وحمايتها عند استخدامك
        لخدمات التسجيل والحجز والأنشطة.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">المعلومات التي نجمعها</h2>
      <ul className="list-disc space-y-2 pr-6">
        <li>بيانات التسجيل: الاسم، رقم الجوال، البريد الإلكتروني، والمعلومات المقدمة في استمارة التسجيل.</li>
        <li>المعلومات الصحية التي تُفصح عنها طوعًا لضمان مناسبة الأنشطة لحالتك.</li>
        <li>بيانات الاشتراك والمدفوعات اللازمة لإتمام عملية الدفع عبر البوابة.</li>
        <li>الصور ومقاطع الفيديو الناتجة عن توثيق الأنشطة (وفقًا للبند العاشر من شروط العضوية).</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">كيف نستخدم معلوماتك</h2>
      <ul className="list-disc space-y-2 pr-6">
        <li>إدارة عضويتك وتسجيلك في الأنشطة والاشتراكات.</li>
        <li>التواصل معك بشأن حجوزاتك والإشعارات المتعلقة بالخدمة.</li>
        <li>ضمان الصحة والسلامة أثناء ممارسة الأنشطة.</li>
        <li>توثيق أنشطة النادي ونشرها إعلاميًا بموجب الموافقة الواردة في شروط العضوية.</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">التوثيق والنشر الإعلامي</h2>
      <p>
        يحق لأندية الحي توثيق أنشطتها ونشرها عبر مختلف الوسائل الإعلامية ومنصات
        التواصل الاجتماعي، ويُعد حضور أو مشاركة الأفراد موافقة صريحة على التصوير
        والنشر دون أي مطالبات بحقوق أو تعويضات.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">حماية البيانات والمدفوعات</h2>
      <p>
        تتم معالجة المدفوعات بشكل آمن عبر مزوّد الدفع المعتمد في المنصة، ولا نقوم
        بتخزين بيانات بطاقتك. نتخذ إجراءات تقنية وتنظيمية معقولة لحماية معلوماتك
        الشخصية.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">حقوقك</h2>
      <p>
        يحق لك الوصول إلى بياناتك الشخصية وتصحيحها أو طلب حذفها وفقًا لنظام حماية
        البيانات الشخصية في المملكة العربية السعودية. للتواصل بشأن خصوصيتك:
        info@nadena-nc.com.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">التعديلات</h2>
      <p>
        قد نحدّث سياسة الخصوصية هذه من وقت لآخر، ويُعد استمرارك في استخدام خدماتنا
        بعد التحديث موافقة على السياسة المعدّلة.
      </p>
    </div>
  );
}

function EnglishPrivacy() {
  return (
    <div className="prose prose-lg dark:prose-invert max-w-none">
      <h2 className="mt-8 mb-4 text-2xl font-bold">Introduction</h2>
      <p>
        This Privacy Policy explains how Nadena (operated by Tatweer Educational
        Services Company) collects, uses, and protects your personal information
        when you use our registration, booking, and activity services.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Information We Collect</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Registration data: name, mobile number, email, and information provided in the registration form.</li>
        <li>Health information you voluntarily disclose to ensure activities are suitable for your condition.</li>
        <li>Subscription and payment data needed to complete payment through the portal.</li>
        <li>Photos and videos produced when documenting activities (per clause 10 of the membership terms).</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">How We Use Your Information</h2>
      <ul className="list-disc space-y-2 pl-6">
        <li>Managing your membership and registering you for activities and subscriptions.</li>
        <li>Communicating with you about your bookings and service-related notifications.</li>
        <li>Ensuring health and safety while activities are practised.</li>
        <li>Documenting and publishing club activities in media under the consent in the membership terms.</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Documentation &amp; Media Publication</h2>
      <p>
        The Neighborhood Clubs reserve the right to document and publish their
        activities across various media outlets and social platforms. Attendance or
        participation is deemed explicit consent to photography and publication, with
        no claims to rights or compensation.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Data &amp; Payment Protection</h2>
      <p>
        Payments are processed securely through the platform’s approved payment
        provider, and we do not store your card details. We apply reasonable
        technical and organisational measures to protect your personal information.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Your Rights</h2>
      <p>
        You have the right to access, correct, or request deletion of your personal
        data in accordance with the Personal Data Protection Law of the Kingdom of
        Saudi Arabia. For privacy enquiries: info@nadena-nc.com.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">Changes</h2>
      <p>
        We may update this Privacy Policy from time to time. Continued use of our
        services after an update constitutes acceptance of the revised policy.
      </p>
    </div>
  );
}
